import { initFirebase } from '../firebase'
import { collection, getDocs, query, orderBy, limit, where, doc, getDoc, onSnapshot } from 'firebase/firestore'

const { db } = initFirebase()

// Natal Chart Reading report — queue, results, cost, errors.
//   reports (reportType == 'natal_chart')  — the live queue mirror + results.
//     Watched in real time (single-field where on reportType, no composite index);
//     queue vs recent are derived client-side.
//   reportStats/natal_chart + reportStats/natal_chart_teaser — write-time rollups.
//   report_generations (reportType == 'natal_chart') — per-generation cost rows.
//   system_logs — error/warn logs for the natal-chart functions.
// Same access path as /year-ahead (ryomen rules allow authed reads; app signs in
// anonymously). Collection queries are orderBy(createdAt)+limit with reportType
// filtered client-side so no composite index is needed.

// 15 parts, in generation order.
export const NATAL_PARTS = [
  'synthesis',
  'chapter_01', 'chapter_02', 'chapter_03', 'chapter_04', 'chapter_05', 'chapter_06',
  'chapter_07', 'chapter_08', 'chapter_09', 'chapter_10', 'chapter_11',
  'fact_check', 'action_guide', 'pdf'
]

const ERROR_FNS = ['previewNatalChartTeaser', 'generateNatalChartReport', 'processNatalChartReport']

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

function toMs(v) {
  if (!v) return 0
  if (typeof v === 'object' && typeof v.toMillis === 'function') return v.toMillis()
  if (typeof v === 'object' && (v.seconds || v._seconds)) return (v.seconds || v._seconds) * 1000
  const t = new Date(v).getTime()
  return isNaN(t) ? 0 : t
}

function normStats(x) {
  return {
    count: num(x.count),
    completed: num(x.completed),
    partial: num(x.partial),
    failed: num(x.failed),
    repairs: num(x.repairs),
    totalCostUSD: num(x.totalCostUSD),
    totalMs: num(x.totalMs),
    totalInputTokens: num(x.totalInputTokens),
    totalOutputTokens: num(x.totalOutputTokens),
    lastAtMs: toMs(x.lastAt)
  }
}

function mapReport(d) {
  const x = d.data()
  const g = x.generation || {}
  const readyAt = (g.partsReadyAt && typeof g.partsReadyAt === 'object') ? g.partsReadyAt : {}
  const partsReadyAtMs = {}
  Object.keys(readyAt).forEach(k => { partsReadyAtMs[k] = toMs(readyAt[k]) })
  return {
    id: d.id,
    reportId: x.reportId || d.id,
    userId: x.userId || '',
    language: x.language || '',
    title: x.title || '',
    person: x.person || {},
    bigThree: x.bigThree || {},
    teaserId: x.teaserId || null,
    purchase: x.purchase || null,
    pdf: x.pdf || null,
    chapters: (x.content && Array.isArray(x.content.chapters))
      ? x.content.chapters.map(c => ({ num: c.num || '', title: c.title || '' }))
      : [],
    createdAtMs: toMs(x.createdAt) || toMs(x.purchasedAt),
    state: g.state || '',
    parts: (g.parts && typeof g.parts === 'object') ? g.parts : {},
    partsReadyAtMs,
    queuedAtMs: toMs(g.queuedAt),
    startedAtMs: toMs(g.startedAt),
    finishedAtMs: toMs(g.finishedAt),
    durationMs: num(g.durationMs),
    costUsd: num(g.costUsd),
    inputTokens: num(g.inputTokens),
    outputTokens: num(g.outputTokens),
    failed: Array.isArray(g.failed) ? g.failed : []
  }
}

function mapGen(d) {
  const x = d.data()
  return {
    id: d.id,
    reportId: x.reportId || '',
    userId: x.userId || '',
    language: x.language || '',
    state: x.state || '',
    partialRepair: x.partialRepair === true,
    hadTeaser: x.hadTeaser === true,
    promptVersion: x.promptVersion || '',
    totalMs: num(x.totalMs),
    inputTokens: num(x.inputTokens),
    outputTokens: num(x.outputTokens),
    thinkingTokens: num(x.thinkingTokens),
    costUsd: num(x.costUsd),
    slices: (x.slices && typeof x.slices === 'object') ? x.slices : {},
    slicesFailed: Array.isArray(x.slicesFailed) ? x.slicesFailed : [],
    createdAtMs: toMs(x.createdAt) || toMs(x.timestamp)
  }
}

export class FirebaseNatalChartService {
  // Real-time watch of all natal_chart reports. Returns the unsubscribe fn.
  watchReports(onData, onError) {
    const q = query(collection(db, 'reports'), where('reportType', '==', 'natal_chart'))
    return onSnapshot(
      q,
      snap => onData(snap.docs.map(mapReport)),
      err => { if (onError) onError(err) }
    )
  }

  async getStats() {
    const [r, t] = await Promise.all([
      getDoc(doc(db, 'reportStats', 'natal_chart')),
      getDoc(doc(db, 'reportStats', 'natal_chart_teaser'))
    ])
    return {
      report: r.exists() ? normStats(r.data()) : null,
      teaser: t.exists() ? normStats(t.data()) : null
    }
  }

  async getGenerations(max = 500) {
    const snap = await getDocs(query(collection(db, 'report_generations'), orderBy('createdAt', 'desc'), limit(max)))
    return snap.docs
      .filter(d => d.data().reportType === 'natal_chart')
      .map(mapGen)
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
  }

  async getErrorLogs(max = 300) {
    const snap = await getDocs(query(collection(db, 'system_logs'), orderBy('createdAt', 'desc'), limit(max)))
    const cutoff = Date.now() - 7 * 86400000
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(x => ERROR_FNS.includes(x.functionName) && (x.level === 'error' || x.level === 'warn') && toMs(x.createdAt) >= cutoff)
      .map(x => ({
        id: x.id,
        level: x.level,
        functionName: x.functionName,
        message: x.message || '',
        error: x.error || '',
        reportId: (x.metadata && x.metadata.reportId) || '',
        createdAtMs: toMs(x.createdAt)
      }))
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
  }
}
